import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { collection, doc, setDoc, onSnapshot, orderBy, query, limit, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { Icons } from '../utils/icons';
import { ROLES } from '../data/users';

// ─── Trabajadores (fallback hasta que carga Firestore) ────────────────────────
const TRABAJADORES_FALLBACK = [
  { id: 'rafael.zapata',    nombre: 'Rafael Zapata' },
  { id: 'reyes.perez',      nombre: 'Reyes Pérez' },
  { id: 'vanessa.escudero', nombre: 'Vanessa Escudero' },
];

// ─── Constantes ───────────────────────────────────────────────────────────────
const SYNC_INTERVAL_MS = 60000;
const COL_ESTADOS      = 'crm_estados';
const COL_ENVIADOS     = 'crm_enviados';
const MAX_CACHE        = 300;

const FOLDER_META = {
  inbox:   { label: 'Entrada',    emoji: '📥' },
  sent:    { label: 'Enviados',   emoji: '📤' },
  drafts:  { label: 'Borradores', emoji: '📝' },
  archive: { label: 'Archivo',    emoji: '🗂️' },
  spam:    { label: 'Spam',       emoji: '🚫' },
  trash:   { label: 'Papelera',   emoji: '🗑️' },
};

const STATUS = {
  pendiente:   { label: 'Pendiente',   cls: 'bg-yellow-100 text-yellow-700 border-yellow-200', dot: 'bg-yellow-400' },
  en_gestion:  { label: 'En gestión',  cls: 'bg-blue-100   text-blue-700   border-blue-200',   dot: 'bg-blue-400'   },
  reclamacion: { label: 'Reclamación', cls: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-400' },
  resuelto:    { label: 'Resuelto',    cls: 'bg-green-100  text-green-700  border-green-200',  dot: 'bg-green-400'  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const timeAgo = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  if (diff < 60000)     return 'ahora';
  if (diff < 3600000)   return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000)  return `${Math.floor(diff / 3600000)}h`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d`;
  return new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

const remitente     = (from) => from?.name || from?.address || 'Desconocido';
const toDocId       = (id)   => (id || 'unknown').replace(/[<>]/g, '').replace(/[^a-zA-Z0-9_\-@.]/g, '_').slice(0, 200) || 'unknown';
// assignedTo puede ser string (legacy) o array — siempre devuelve array
const getAssignedTo = (email) => { const a = email?.assignedTo; if (!a) return []; return Array.isArray(a) ? a : [a]; };
const sessionKey = (f)    => `crm_imap_${(f || 'INBOX').replace(/[^a-zA-Z0-9]/g, '_')}`;
const stripRe    = (s)    => (s || '').replace(/^(Re|Fwd?|AW|RES|SV|VS|TR|WG):\s*/i, '').trim();

function loadFromSession(folder) {
  try { const c = sessionStorage.getItem(sessionKey(folder)); return c ? JSON.parse(c) : []; }
  catch { return []; }
}
function saveToSession(folder, emails) {
  try { sessionStorage.setItem(sessionKey(folder), JSON.stringify(emails.slice(0, MAX_CACHE))); } catch {}
}

// ─── Agrupación en hilos ──────────────────────────────────────────────────────
function buildThreads(emails) {
  // Cada email pertenece al hilo cuyo root es: references[0] > inReplyTo > messageId propio
  const rootOf = (e) => e.references?.[0] || e.inReplyTo || e.messageId;

  const map = new Map();
  emails.forEach(email => {
    const root = rootOf(email);
    if (!map.has(root)) map.set(root, []);
    map.get(root).push(email);
  });

  return Array.from(map.values())
    .map(msgs => {
      const sorted     = [...msgs].sort((a, b) => new Date(a.date) - new Date(b.date));
      const latest     = sorted[sorted.length - 1];
      const subject    = stripRe(sorted[0].subject);
      const addresses  = [...new Set(sorted.map(e => e.from?.address).filter(Boolean))];
      const names      = [...new Set(sorted.map(e => remitente(e.from)).filter(Boolean))];
      const hasUnread      = sorted.some(e => !e.read);
      const isFactura        = sorted.some(e => e.triage?.isFactura || e.isFactura);
      const isReclamacion    = sorted.some(e => e.triage?.isReclamacion || /\breclamaci[oó]n\b/i.test(e.subject || ''));
      const isWebIncidencia  = sorted.some(e => e.triage?.isWebIncidencia || /nueva\s+incidencia\b/i.test(e.subject || ''));
      const ARISTON_ADDRS = ['service.callcenter.es@ariston.com', 'info@aristonsat.es'];
      const isAriston        = sorted.some(e => e.triage?.isAriston || ARISTON_ADDRS.includes((e.from?.address || '').toLowerCase()));
      const INFORMATIVO_ADDRS_FE = ['no-responda@ariston.com'];
      const isInformativo    = !isAriston && sorted.some(e =>
        e.triage?.isInformativo ||
        INFORMATIVO_ADDRS_FE.includes((e.from?.address || '').toLowerCase()) ||
        /\b(dhl|adt|cbl)\b/i.test(e.from?.name || '') ||
        /[@.]dhl\.|[@.]adt\.|[@.]amazon\./i.test(e.from?.address || ''));
      // Si se detecta reclamación y el estado manual aún es pendiente, sobreescribimos latest
      const effectiveStatus = (isReclamacion && (!latest.status || latest.status === 'pendiente'))
        ? 'reclamacion'
        : latest.status;
      const threadLatest = effectiveStatus !== latest.status
        ? { ...latest, status: effectiveStatus }
        : latest;
      return {
        id:          sorted[0].messageId,
        subject,
        emails:      sorted,
        latest:      threadLatest,
        latestDate:  threadLatest.date,
        hasUnread,
        names,
        addresses,
        count:       sorted.length,
        status:      effectiveStatus,
        assignedTo:  threadLatest.assignedTo,
        starred:     sorted.some(e => e.starred),
        triage:      { ...(threadLatest.triage || {}), isFactura, isReclamacion, isWebIncidencia, isAriston, isInformativo },
        isFactura,
        isReclamacion,
        isWebIncidencia,
        isAriston,
        isInformativo,
      };
    })
    .sort((a, b) => new Date(b.latestDate) - new Date(a.latestDate));
}

// ─── Autocomplete de destinatarios ────────────────────────────────────────────
function RecipientSuggestions({ suggestions, onSelect }) {
  return (
    <ul className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
      {suggestions.map((c, i) => (
        <li key={i}>
          <button type="button" onMouseDown={() => onSelect(c)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-red-50 transition-colors">
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0">
              {(c.name || c.address).charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              {c.name && <p className="text-xs font-medium text-gray-800 truncate">{c.name}</p>}
              <p className="text-xs text-gray-500 truncate">{c.address}</p>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

// ─── Componente ───────────────────────────────────────────────────────────────
export default function CRMEmailView({ onNavigate }) {
  const { user } = useAuth();

  // ── Carpetas ───────────────────────────────────────────────────────────────
  const [folders, setFolders]             = useState([]);
  const [currentFolder, setCurrentFolder] = useState('INBOX');
  const [loadingFolders, setLoadingFolders] = useState(true);
  const sentFolderRef = useRef(null); // path de la carpeta Sent IMAP

  // ── Emails por carpeta ─────────────────────────────────────────────────────
  const [emailsByFolder, setEmailsByFolder] = useState({ INBOX: loadFromSession('INBOX') });
  const currentEmails = emailsByFolder[currentFolder] || [];

  // ── Overlay Firestore ──────────────────────────────────────────────────────
  const [estados, setEstados]   = useState({});
  const [enviados, setEnviados] = useState([]);

  // ── Sync ───────────────────────────────────────────────────────────────────
  const [syncing, setSyncing]         = useState(false);
  const [syncError, setSyncError]     = useState(null);
  const [lastSync, setLastSync]       = useState(null);
  const [hasMore, setHasMore]             = useState(false);
  const [loadingMore, setLoadingMore]     = useState(false);
  const [noMasHistorial, setNoMasHistorial] = useState(false);
  const lastUidRef       = useRef(0);
  const timerRef         = useRef(null);
  const catchUpTimerRef  = useRef(null);
  const syncEmailsRef    = useRef(null);
  const estadosRef       = useRef({});

  // ── Hilo seleccionado ──────────────────────────────────────────────────────
  const [selectedThread, setSelectedThread] = useState(null);
  const [expandedEmails, setExpandedEmails] = useState(new Set());
  const [threadBodies, setThreadBodies]     = useState({});   // { messageId: bodyData }
  const [loadingBodies, setLoadingBodies]   = useState({});   // { messageId: bool }
  const [downloadingAtt, setDownloadingAtt] = useState(null); // `${messageId}-${index}`

  // ── Filtros / búsqueda ────────────────────────────────────────────────────
  const [busqueda, setBusqueda]             = useState('');
  const [filtroStatus, setFiltroStatus]     = useState('todos');
  const [filtroAsignado, setFiltroAsignado] = useState('todos');
  const [filtroTriage, setFiltroTriage]     = useState(null);
  const [showMarketing, setShowMarketing]   = useState(false);
  const [displayLimit, setDisplayLimit]     = useState(20);
  const [showFacturasResueltas, setShowFacturasResueltas] = useState(false);

  // ── Redactar ───────────────────────────────────────────────────────────────
  const [composing, setComposing]         = useState(null);
  const [compose, setCompose]             = useState({ to: '', cc: '', bcc: '', subject: '', body: '', attachments: [], forwardedHtml: '' });
  const [showCc, setShowCc]               = useState(false);
  const [showBcc, setShowBcc]             = useState(false);
  const [sending, setSending]             = useState(false);
  const [sendError, setSendError]         = useState('');
  const [forwardLoading, setForwardLoading]     = useState(false);
  const [fetchingAttachments, setFetchingAttachments] = useState(false);
  const [pendingForward, setPendingForward] = useState(null);
  const [pendingFactura, setPendingFactura] = useState(null);
  const [procesandoFactura, setProcesandoFactura] = useState(false);
  const fileInputRef                  = useRef(null);
  const [threadViewMode, setThreadViewMode] = useState(() =>
    localStorage.getItem('crm_thread_view') || 'chat'
  );

  // ── Equipo dinámico desde Firestore (vía función) ─────────────────────────
  const [trabajadores, setTrabajadores] = useState(TRABAJADORES_FALLBACK);
  useEffect(() => {
    fetch('/.netlify/functions/manageUsers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
      body: JSON.stringify({ action: 'list' }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && Array.isArray(data.data) && data.data.length > 0) {
          const OFFICE_ROLES = ['admin', 'atencion_cliente', 'coordinador_operaciones'];
          const filtered = data.data
            .filter(u => OFFICE_ROLES.includes(u.role))
            .map(u => ({ id: u.id, nombre: u.name }));
          if (filtered.length > 0) setTrabajadores(filtered);
        }
      })
      .catch(() => {});
  }, []);

  // ── Selección múltiple ────────────────────────────────────────────────────
  const [selectedThreadIds, setSelectedThreadIds] = useState(new Set());
  const [bulkApplying, setBulkApplying]           = useState(false);
  const [bulkStatus, setBulkStatus]               = useState('');
  const [bulkAsignado, setBulkAsignado]           = useState('');

  // ── Autocomplete de destinatarios ──────────────────────────────────────────
  const [acField, setAcField]   = useState(null);   // 'to' | 'cc' | 'bcc' | null
  const [acQuery, setAcQuery]   = useState('');      // último fragmento escrito

  // ── StelOrder ──────────────────────────────────────────────────────────────
  const [cliente, setCliente]               = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [showCliente, setShowCliente]       = useState(false);

  // ── Cargar carpetas IMAP ───────────────────────────────────────────────────
  useEffect(() => {
    fetch('/.netlify/functions/getImapFolders', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => {
        if (!d.folders) return;
        setFolders(d.folders);
        const sent = d.folders.find(f => f.specialUse === 'sent');
        if (sent) sentFolderRef.current = sent.path;
      })
      .catch(() => {})
      .finally(() => setLoadingFolders(false));
  }, []);

  // ── Suscripción Firestore: estados ─────────────────────────────────────────
  useEffect(() => {
    // `crm_estados` crece 1 doc por email procesado. El overlay solo necesita
    // los de la bandeja actual → los 3000 más recientes por createdAt.
    const qEst = query(collection(db, COL_ESTADOS), orderBy('createdAt', 'desc'), limit(3000));
    const unsub = onSnapshot(qEst, snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = d.data(); });
      estadosRef.current = map;
      setEstados(map);
    });
    return () => unsub();
  }, []);

  // ── Suscripción Firestore: enviados ────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, COL_ENVIADOS), orderBy('date', 'desc'), limit(100));
    const unsub = onSnapshot(q, snap => {
      setEnviados(snap.docs.map(d => ({ ...d.data(), _docId: d.id, folder: 'SENT' })));
    });
    return () => unsub();
  }, []);

  // ── Cargar lastUid (solo INBOX) ────────────────────────────────────────────
  useEffect(() => {
    getDoc(doc(db, 'crm_meta', 'sync'))
      .then(d => {
        if (d.exists()) {
          lastUidRef.current = d.data().lastUid || 0;
          setLastSync(d.data().lastSync);
          // Si hay un lastUid guardado, lanzar catch-up UID inmediato tras el sync inicial
          if (lastUidRef.current > 0) {
            setTimeout(() => syncEmailsRef.current?.(false, 'INBOX'), 3000);
          }
        }
      })
      .catch(() => {});
  }, []);

  // ── Sync IMAP ──────────────────────────────────────────────────────────────
  const syncEmails = useCallback(async (force = false, folder = currentFolder) => {
    if (syncing && !force) return;
    setSyncing(true); setSyncError(null);
    try {
      const isInbox     = folder.toUpperCase() === 'INBOX';
      const wasUidBased = isInbox && lastUidRef.current > 0;
      const params      = new URLSearchParams({
        folder,
        lastUid: isInbox ? String(lastUidRef.current) : '0',
        days:    wasUidBased ? '2' : '90',
      });
      const res  = await fetch(`/.netlify/functions/syncEmails?${params}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (!data.success) { setSyncError(data.error || 'Error de sync'); return; }
      if (!data.emails?.length) return;

      setEmailsByFolder(prev => {
        const existing = new Map((prev[folder] || []).map(e => [e.messageId, e]));
        data.emails.forEach(e => existing.set(e.messageId, e));
        const sorted = Array.from(existing.values()).sort((a, b) => new Date(b.date) - new Date(a.date));
        saveToSession(folder, sorted);
        return { ...prev, [folder]: sorted };
      });

      // Auto-crear estado pendiente para emails nuevos
      const nuevos = data.emails.filter(e => !estadosRef.current[toDocId(e.messageId)]);
      await Promise.all(nuevos.map(e =>
        setDoc(doc(db, COL_ESTADOS, toDocId(e.messageId)), {
          messageId: e.messageId, status: 'pendiente', createdAt: new Date().toISOString(),
        }, { merge: true }).catch(() => {})
      ));

      if (isInbox && data.lastUid > lastUidRef.current) {
        lastUidRef.current = data.lastUid;
        const now = new Date().toISOString();
        setLastSync(now);
        await setDoc(doc(db, 'crm_meta', 'sync'), { lastUid: data.lastUid, lastSync: now }, { merge: true });
      }
      // Tras cualquier carga con emails, mostrar botón de historial.
      // Solo cargarMas puede fijarlo en false cuando realmente no hay más.
      setHasMore(true);

      // Catch-up rápido: si el sync UID llenó el lote completo hay más emails sin cargar
      if (wasUidBased && data.hasMore) {
        clearTimeout(catchUpTimerRef.current);
        catchUpTimerRef.current = setTimeout(() => syncEmailsRef.current?.(false, folder), 2000);
      }
    } catch (err) { setSyncError(err.message); }
    finally { setSyncing(false); }
  }, [syncing, currentFolder]);

  // Mantiene syncEmailsRef apuntando siempre a la versión más reciente
  useEffect(() => { syncEmailsRef.current = syncEmails; }, [syncEmails]);

  // ── Sync Sent IMAP en background (sin afectar al spinner del UI) ─────────
  const syncSentBackground = useCallback(async () => {
    const sentPath = sentFolderRef.current;
    if (!sentPath) return;
    try {
      const cached = loadFromSession(sentPath);
      if (cached.length) setEmailsByFolder(prev => ({ ...prev, [sentPath]: cached }));

      const params = new URLSearchParams({ folder: sentPath, lastUid: '0', days: '30' });
      const res  = await fetch(`/.netlify/functions/syncEmails?${params}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (!data.success || !data.emails?.length) return;

      setEmailsByFolder(prev => {
        const existing = new Map((prev[sentPath] || []).map(e => [e.messageId, e]));
        data.emails.forEach(e => existing.set(e.messageId, e));
        const sorted = Array.from(existing.values()).sort((a, b) => new Date(b.date) - new Date(a.date));
        saveToSession(sentPath, sorted);
        return { ...prev, [sentPath]: sorted };
      });
    } catch { /* silencioso */ }
  }, []); // eslint-disable-line

  // Cargar más emails antiguos
  const cargarMas = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const folder  = currentFolder;
      const current = emailsByFolder[folder] || [];
      const oldest    = current.reduce((min, e) =>
        !min || new Date(e.date) < new Date(min) ? e.date : min, null);
      const oldestUid = current.reduce((min, e) =>
        e.uid && (!min || e.uid < min) ? e.uid : min, null);
      const params = new URLSearchParams({
        folder,
        lastUid: '0',
        days:    '365',
        ...(oldestUid ? { beforeUid: String(oldestUid) } : oldest ? { before: oldest } : {}),
      });
      const res  = await fetch(`/.netlify/functions/syncEmails?${params}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (!data.success) {
        setSyncError('No se pudieron cargar emails más antiguos. Inténtalo de nuevo.');
        return;
      }
      if (!data.emails?.length) { setHasMore(false); setNoMasHistorial(true); return; }

      setEmailsByFolder(prev => {
        const existing = new Map((prev[folder] || []).map(e => [e.messageId, e]));
        data.emails.forEach(e => existing.set(e.messageId, e));
        const sorted = Array.from(existing.values()).sort((a, b) => new Date(b.date) - new Date(a.date));
        saveToSession(folder, sorted);
        return { ...prev, [folder]: sorted };
      });
      setHasMore(data.hasMore || false);
    } catch (err) { setSyncError(err.message); }
    finally { setLoadingMore(false); }
  }, [loadingMore, currentFolder, emailsByFolder]);

  // Sync al cambiar carpeta + polling
  useEffect(() => {
    setHasMore(false);
    setNoMasHistorial(false);
    if (!emailsByFolder[currentFolder]?.length) {
      const cached = loadFromSession(currentFolder);
      if (cached.length) setEmailsByFolder(prev => ({ ...prev, [currentFolder]: cached }));
    }
    syncEmails(true, currentFolder);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => syncEmails(false, currentFolder), SYNC_INTERVAL_MS);
    setSelectedThreadIds(new Set());
    return () => { clearInterval(timerRef.current); clearTimeout(catchUpTimerRef.current); };
  }, [currentFolder]); // eslint-disable-line

  // Sincronizar Sent cuando se cargan las carpetas (una vez) y cada 5 minutos
  const sentTimerRef = useRef(null);
  useEffect(() => {
    if (!folders.length) return;
    syncSentBackground();
    clearInterval(sentTimerRef.current);
    sentTimerRef.current = setInterval(syncSentBackground, 5 * 60 * 1000);
    return () => clearInterval(sentTimerRef.current);
  }, [folders.length]); // eslint-disable-line

  // ── Emails con overlay aplicado ────────────────────────────────────────────
  const emailsMerged = useMemo(() => {
    let base;
    if (currentFolder === '__SENT__') {
      base = enviados;
    } else if (currentFolder.toUpperCase() === 'INBOX') {
      // Mezclar INBOX + enviados Firestore + enviados IMAP Sent folder
      const map = new Map();
      currentEmails.forEach(e => map.set(e.messageId, e));
      enviados.forEach(e => { if (!map.has(e.messageId)) map.set(e.messageId, e); });
      const sentPath = sentFolderRef.current;
      if (sentPath) {
        (emailsByFolder[sentPath] || []).forEach(e => {
          if (!map.has(e.messageId)) map.set(e.messageId, e);
        });
      }
      base = Array.from(map.values());
    } else {
      base = currentEmails;
    }
    return base.map(email => {
      const docId   = toDocId(email.messageId);
      const overlay = estados[docId] || {};
      const merged  = { ...email, ...overlay, _docId: docId };
      // Aplicar overrides manuales sobre el triage
      if (overlay.isMarketing !== undefined || overlay.isFactura !== undefined) {
        merged.triage = {
          ...(email.triage || {}),
          ...(overlay.isMarketing !== undefined ? { isMarketing: overlay.isMarketing, manual: true } : {}),
          ...(overlay.isFactura  !== undefined ? { isFactura:  overlay.isFactura               } : {}),
        };
      }
      return merged;
    });
  }, [currentEmails, enviados, estados, currentFolder, emailsByFolder]);

  // ── Contactos conocidos para autocomplete ─────────────────────────────────
  const knownContacts = useMemo(() => {
    const OUR_DOMAIN = 'climatizacionzapata';
    const map = new Map(); // address -> { address, name, score }
    const allEmails = [
      ...Object.values(emailsByFolder).flat(),
      ...enviados,
    ];
    allEmails.forEach(email => {
      const ts = email.date ? new Date(email.date).getTime() : 0;
      const recency = ts > Date.now() - 30 * 24 * 60 * 60 * 1000 ? 3 : ts > Date.now() - 90 * 24 * 60 * 60 * 1000 ? 2 : 1;
      const addContact = (addr) => {
        if (!addr?.address || addr.address.includes(OUR_DOMAIN)) return;
        const key = addr.address.toLowerCase();
        const prev = map.get(key) || { address: addr.address, name: addr.name || '', score: 0 };
        map.set(key, { ...prev, name: prev.name || addr.name || '', score: prev.score + recency });
      };
      [email.from, ...(email.to || []), ...(email.cc || [])].forEach(addContact);
    });
    return Array.from(map.values()).sort((a, b) => b.score - a.score);
  }, [emailsByFolder, enviados]);

  // ── Hilos ──────────────────────────────────────────────────────────────────
  const threads = useMemo(() => buildThreads(emailsMerged), [emailsMerged]);

  // Sincronizar hilo seleccionado cuando cambia el overlay (leídos, estado, etc.)
  useEffect(() => {
    if (!selectedThread) return;
    const updated = threads.find(t => t.id === selectedThread.id);
    if (updated) setSelectedThread(updated);
  }, [threads]); // eslint-disable-line

  const facturasCount = useMemo(() =>
    threads.filter(t => t.isFactura && t.latest.status !== 'resuelto').length,
  [threads]);

  const threadsFiltrados = useMemo(() => {
    return threads.filter(t => {
      if (!showMarketing && t.triage?.isMarketing)                                    return false;
      if (!showFacturasResueltas && t.isFactura && t.latest.status === 'resuelto')    return false;
      if (filtroStatus === 'no_leidos' && !t.hasUnread)                                return false;
      if (filtroStatus !== 'todos' && filtroStatus !== 'no_leidos' && t.latest.status !== filtroStatus) return false;
      if (filtroAsignado === 'sin_asignar' && getAssignedTo(t.latest).length > 0)       return false;
      if (filtroAsignado !== 'todos' && filtroAsignado !== 'sin_asignar' && !getAssignedTo(t.latest).includes(filtroAsignado)) return false;
      if (filtroTriage === 'ariston' && !t.isAriston)       return false;
      if (filtroTriage === 'factura' && !t.isFactura)       return false;
      if (filtroTriage === 'web'     && !t.isWebIncidencia) return false;
      if (busqueda) {
        const q = busqueda.toLowerCase();
        return t.subject.toLowerCase().includes(q) ||
               t.addresses.some(a => a.toLowerCase().includes(q)) ||
               t.names.some(n => n.toLowerCase().includes(q));
      }
      return true;
    });
  }, [threads, showMarketing, filtroStatus, filtroAsignado, filtroTriage, busqueda]);

  // Resetear paginación al cambiar filtros o carpeta
  useEffect(() => { setDisplayLimit(20); },
    [showMarketing, filtroStatus, filtroAsignado, filtroTriage, busqueda, currentFolder]); // eslint-disable-line

  const noLeidos  = useMemo(() =>
    (emailsByFolder['INBOX'] || []).filter(e => !estadosRef.current[toDocId(e.messageId)]?.read && !e.read && !e.triage?.isMarketing).length,
  [emailsByFolder, estados]); // eslint-disable-line
  const marketing = threads.filter(t => t.triage?.isMarketing).length;

  // ── Conteos por estado para el panel lateral ──────────────────────────────
  const statusCounts = useMemo(() => {
    const visible = threads.filter(t => !t.triage?.isMarketing && !t.isInformativo);
    const counts  = { total: visible.length };
    Object.keys(STATUS).forEach(k => {
      counts[k] = visible.filter(t => (t.latest?.status || 'pendiente') === k).length;
    });
    return counts;
  }, [threads]);

  // ── Conteos por miembro del equipo ────────────────────────────────────────
  const teamCounts = useMemo(() => {
    const visible = threads.filter(t => !t.triage?.isMarketing && !t.isInformativo && (t.latest?.status || 'pendiente') !== 'resuelto');
    const counts  = { total: visible.length, sinAsignar: 0 };
    trabajadores.forEach(w => { counts[w.id] = 0; });
    visible.forEach(t => {
      const assigned = getAssignedTo(t.latest);
      if (assigned.length > 0) {
        assigned.forEach(id => { if (counts[id] !== undefined) counts[id]++; });
      } else {
        counts.sinAsignar++;
      }
    });
    return counts;
  }, [threads]);

  // ── Conteos triage (sin resueltos) ───────────────────────────────────────────
  const triageCounts = useMemo(() => {
    const pending = threads.filter(t => !t.triage?.isMarketing && (t.latest?.status || 'pendiente') !== 'resuelto');
    return {
      ariston: pending.filter(t => t.isAriston).length,
      factura: pending.filter(t => t.isFactura).length,
      web:     pending.filter(t => t.isWebIncidencia).length,
    };
  }, [threads]);

  // ── Tiempo medio de respuesta (últimos 7 días, solo hilos contestados) ──────
  const statsRespuesta = useMemo(() => {
    const OUR_DOMAIN  = 'climatizacionzapata';
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const isOurs = (email) => (email.from?.address || '').includes(OUR_DOMAIN);

    const tiempos = threads
      .filter(t => !t.triage?.isMarketing)
      .flatMap(t => {
        // Solo hilos cuyo primer mensaje externo entró en los últimos 7 días
        const primerExterno = t.emails.find(e => !isOurs(e));
        if (!primerExterno || new Date(primerExterno.date).getTime() < sevenDaysAgo) return [];
        // Primera respuesta nuestra después del primer externo
        const primeraRespuesta = t.emails.find(
          e => isOurs(e) && new Date(e.date) > new Date(primerExterno.date)
        );
        if (!primeraRespuesta) return [];
        const ms = new Date(primeraRespuesta.date) - new Date(primerExterno.date);
        return ms > 0 ? [ms] : [];
      });

    if (!tiempos.length) return null;

    const mediaMs = tiempos.reduce((s, t) => s + t, 0) / tiempos.length;
    const horas   = Math.floor(mediaMs / 3600000);
    const minutos = Math.floor((mediaMs % 3600000) / 60000);
    const texto   = horas >= 24
      ? `${Math.floor(horas / 24)}d ${horas % 24}h`
      : horas > 0 ? `${horas}h ${minutos}m` : `${minutos}m`;

    return { texto, count: tiempos.length };
  }, [threads]);

  // ── Cargar cuerpo de un email ──────────────────────────────────────────────
  const cargarCuerpo = useCallback(async (email) => {
    const key = email.messageId;
    if (threadBodies[key] !== undefined) return;

    // Email enviado desde la app: usar cuerpo guardado en Firestore directamente
    if (!email.uid && email.htmlBody) {
      setThreadBodies(prev => ({ ...prev, [key]: { html: email.htmlBody, text: null, attachments: [] } }));
      return;
    }

    // Si no tiene uid IMAP (enviado sin cuerpo guardado) no hay nada que cargar
    if (!email.uid) {
      setThreadBodies(prev => ({ ...prev, [key]: { error: 'Sin contenido disponible' } }));
      return;
    }

    setLoadingBodies(prev => ({ ...prev, [key]: true }));
    try {
      const params = new URLSearchParams({ uid: email.uid, folder: email.folder || 'INBOX' });
      const res  = await fetch(`/.netlify/functions/getEmailBody?${params}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (data.success) {
        setThreadBodies(prev => ({ ...prev, [key]: data }));
      } else {
        setThreadBodies(prev => ({ ...prev, [key]: { error: data.error || 'Error desconocido' } }));
      }
    } catch (err) {
      setThreadBodies(prev => ({ ...prev, [key]: { error: err.message } }));
    } finally {
      setLoadingBodies(prev => ({ ...prev, [key]: false }));
    }
  }, [threadBodies]);

  // ── Abrir hilo ────────────────────────────────────────────────────────────
  const abrirHilo = useCallback(async (thread) => {
    setSelectedThread(thread);
    setShowCliente(false);
    setCliente(null);

    // Expandir el email más reciente por defecto
    setExpandedEmails(new Set([thread.latest.messageId]));

    // Cargar cuerpo del email más reciente
    cargarCuerpo(thread.latest);

    // Marcar todos los no leídos del hilo como leídos
    const unread = thread.emails.filter(e => !e.read);
    if (unread.length > 0) {
      setSelectedThread(prev => prev
        ? { ...prev, hasUnread: false, emails: prev.emails.map(e => ({ ...e, read: true })) }
        : prev
      );
      await Promise.all(unread.map(e => Promise.all([
        // Firestore overlay
        setDoc(doc(db, COL_ESTADOS, e._docId), {
          messageId: e.messageId, read: true, updatedAt: new Date().toISOString(),
        }, { merge: true }).catch(() => {}),
        // Flag \Seen en IMAP (solo emails con uid)
        e.uid
          ? fetch(`/.netlify/functions/setEmailFlag?${new URLSearchParams({ uid: e.uid, folder: e.folder || 'INBOX', flag: '\\Seen', action: 'add' })}`, { headers: { ...authService.authHeader() } }).catch(() => {})
          : Promise.resolve(),
      ])));
    }
  }, [cargarCuerpo]);

  // ── Expandir / colapsar email individual ──────────────────────────────────
  const toggleExpandEmail = useCallback((email) => {
    setExpandedEmails(prev => {
      const next = new Set(prev);
      if (next.has(email.messageId)) {
        next.delete(email.messageId);
      } else {
        next.add(email.messageId);
        cargarCuerpo(email);
      }
      return next;
    });
  }, [cargarCuerpo]);

  // ── Overlay CRM ───────────────────────────────────────────────────────────
  const actualizarEstado = useCallback(async (email, updates) => {
    await setDoc(doc(db, COL_ESTADOS, email._docId), {
      messageId: email.messageId, ...updates, updatedAt: new Date().toISOString(),
    }, { merge: true }).catch(() => {});
  }, []);

  const aplicarAccionMasiva = useCallback(async () => {
    if (!bulkStatus && !bulkAsignado) return;
    setBulkApplying(true);
    const targets = threadsFiltrados.filter(t => selectedThreadIds.has(t.id));
    await Promise.all(targets.map(t => {
      const updates = {};
      if (bulkStatus)   updates.status     = bulkStatus;
      if (bulkAsignado) updates.assignedTo = [bulkAsignado];
      return actualizarEstado(t.latest, updates);
    }));
    setSelectedThreadIds(new Set());
    setBulkStatus('');
    setBulkAsignado('');
    setBulkApplying(false);
  }, [bulkStatus, bulkAsignado, selectedThreadIds, threadsFiltrados, actualizarEstado]);

  const toggleReadEmail = useCallback((email) => {
    const newRead = !email.read;
    actualizarEstado(email, { read: newRead });
    // Sincronizar flag \Seen en IMAP si el email viene de IMAP (tiene uid)
    if (email.uid) {
      const params = new URLSearchParams({
        uid:    email.uid,
        folder: email.folder || 'INBOX',
        flag:   '\\Seen',
        action: newRead ? 'add' : 'remove',
      });
      fetch(`/.netlify/functions/setEmailFlag?${params}`, { headers: { ...authService.authHeader() } }).catch(() => {});
    }
  }, [actualizarEstado]);

  const toggleStarEmail = useCallback((email) => {
    actualizarEstado(email, { starred: !email.starred });
  }, [actualizarEstado]);

  // ── Descargar adjunto ─────────────────────────────────────────────────────
  const downloadAttachment = async (email, filename, index) => {
    const key = `${email.messageId}-${index}`;
    setDownloadingAtt(key);
    try {
      const params = new URLSearchParams({ uid: email.uid, index, folder: email.folder || 'INBOX' });
      const res  = await fetch(`/.netlify/functions/downloadAttachment?${params}`, { headers: { ...authService.authHeader() } });
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert(`Error al descargar: ${err.message}`); }
    finally { setDownloadingAtt(null); }
  };

  // ── StelOrder ─────────────────────────────────────────────────────────────
  const buscarCliente = async () => {
    const email = selectedThread?.emails[0];
    if (!email?.from?.address) return;
    setLoadingCliente(true); setCliente(null); setShowCliente(true);
    try {
      const res = await fetch(`/.netlify/functions/searchClientByEmail?email=${encodeURIComponent(email.from.address)}`, { headers: { ...authService.authHeader() } });
      setCliente(await res.json());
    } catch { setCliente({ success: false }); }
    finally { setLoadingCliente(false); }
  };

  // ── Abrir redactor ────────────────────────────────────────────────────────
  // ── Helpers de reenvío ───────────────────────────────────────────────────

  // Obtiene el cuerpo de un email: primero del cache, si no lo descarga
  const fetchEmailBody = useCallback(async (email) => {
    const cached = threadBodies[email.messageId];
    if (cached && !cached.error) return cached;
    if (!email.uid && email.htmlBody) return { html: email.htmlBody, text: null, attachments: [] };
    if (!email.uid) return null;
    try {
      const params = new URLSearchParams({ uid: email.uid, folder: email.folder || 'INBOX' });
      const res  = await fetch(`/.netlify/functions/getEmailBody?${params}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (data.success) {
        setThreadBodies(prev => ({ ...prev, [email.messageId]: data }));
        return data;
      }
    } catch {}
    return null;
  }, [threadBodies]);

  // Construye el bloque HTML de la conversación reenviada
  const buildForwardedHtml = (emailsWithBodies) =>
    emailsWithBodies.map(({ email, bodyData }) => {
      const fecha   = new Date(email.date).toLocaleString('es-ES');
      const de      = email.from?.name ? `${email.from.name} &lt;${email.from.address}&gt;` : (email.from?.address || '');
      const para    = (email.to || []).map(t => t.address).join(', ');
      const ccLine  = (email.cc || []).length > 0 ? `<strong>CC:</strong> ${email.cc.map(t => t.address).join(', ')}<br>` : '';
      const content = bodyData?.html
        || (bodyData?.text ? `<pre style="font-family:inherit;white-space:pre-wrap">${bodyData.text.replace(/</g, '&lt;')}</pre>` : '<em style="color:#999">Sin contenido</em>');
      return `<div style="border-left:3px solid #DC2626;padding-left:12px;margin:12px 0;font-family:Arial,sans-serif;font-size:13px;color:#555">
  <div style="margin-bottom:6px;color:#888;font-size:12px;line-height:1.6">
    <strong>De:</strong> ${de}<br><strong>Para:</strong> ${para}<br>${ccLine}
    <strong>Fecha:</strong> ${fecha}<br><strong>Asunto:</strong> ${email.subject || ''}
  </div>
  <div>${content}</div>
</div>`;
    }).join('<hr style="border:none;border-top:1px solid #eee;margin:6px 0">');

  // Recoge los metadatos de adjuntos de todos los emails cargados
  const collectAttachmentInfos = (emailsWithBodies) =>
    emailsWithBodies.flatMap(({ email, bodyData }) =>
      (bodyData?.attachments || []).map((a, index) => ({
        email, index,
        filename:    a.filename    || `adjunto_${index}`,
        size:        a.size        || 0,
        contentType: a.contentType || 'application/octet-stream',
      }))
    );

  // Descarga los adjuntos seleccionados y los convierte a base64
  const fetchAttachmentsForForward = async (attInfos) => {
    return Promise.all(attInfos.map(({ email, index, filename, contentType }) =>
      new Promise(async (resolve) => {
        try {
          const params = new URLSearchParams({ uid: email.uid, index, folder: email.folder || 'INBOX' });
          const res  = await fetch(`/.netlify/functions/downloadAttachment?${params}`, { headers: { ...authService.authHeader() } });
          const blob = await res.blob();
          const reader = new FileReader();
          reader.onload = () => resolve({
            filename, contentType, size: blob.size,
            content: reader.result.split(',')[1],
          });
          reader.readAsDataURL(blob);
        } catch { resolve(null); }
      })
    )).then(results => results.filter(Boolean));
  };

  // Abre el compose con los datos ya resueltos (tras decisión de adjuntos)
  const abrirComposeConReenvio = (subject, forwardedHtml, attachments = []) => {
    setCompose({ to: '', cc: '', bcc: '', subject, body: '', attachments, forwardedHtml });
    setComposing({ mode: 'forward' });
    setShowCc(false); setShowBcc(false);
    setSendError('');
    setPendingForward(null);
  };

  // Reenviar un email concreto del hilo
  const abrirReenvioEmail = useCallback(async (email) => {
    setForwardLoading(true);
    try {
      const bodyData          = await fetchEmailBody(email);
      const emailsWithBodies  = [{ email, bodyData }];
      const forwardedHtml     = buildForwardedHtml(emailsWithBodies);
      const subject           = `Fwd: ${stripRe(email.subject || '')}`;
      const attInfos          = collectAttachmentInfos(emailsWithBodies);

      if (attInfos.length > 0) {
        setPendingForward({ subject, forwardedHtml, attInfos });
      } else {
        abrirComposeConReenvio(subject, forwardedHtml);
      }
    } finally { setForwardLoading(false); }
  }, [fetchEmailBody]); // eslint-disable-line

  // Reenviar el hilo completo (todos los emails en orden)
  const abrirReenvioHilo = useCallback(async (thread) => {
    setForwardLoading(true);
    try {
      const emailsWithBodies = await Promise.all(
        thread.emails.map(async email => ({ email, bodyData: await fetchEmailBody(email) }))
      );
      const forwardedHtml = buildForwardedHtml(emailsWithBodies);
      const subject       = `Fwd: ${thread.subject}`;
      const attInfos      = collectAttachmentInfos(emailsWithBodies);

      if (attInfos.length > 0) {
        setPendingForward({ subject, forwardedHtml, attInfos });
      } else {
        abrirComposeConReenvio(subject, forwardedHtml);
      }
    } finally { setForwardLoading(false); }
  }, [fetchEmailBody]); // eslint-disable-line

  // ── Procesar factura de proveedor ────────────────────────────────────────
  const ADMIN_EMAIL = 'administracion@climatizacionzapata.es';

  const procesarFactura = async (thread) => {
    setProcesandoFactura(true);
    try {
      // Cargar cuerpos de todos los emails del hilo
      const emailsWithBodies = await Promise.all(
        thread.emails.map(async email => ({ email, bodyData: await fetchEmailBody(email) }))
      );

      // Recoger todos los adjuntos del hilo y descargarlos
      const attInfos    = collectAttachmentInfos(emailsWithBodies);
      const attachments = attInfos.length > 0 ? await fetchAttachmentsForForward(attInfos) : [];

      // Construir cuerpo reenviado
      const forwardedHtml = buildForwardedHtml(emailsWithBodies);
      const payload = {
        to:          ADMIN_EMAIL,
        subject:     `Factura proveedor: ${thread.subject}`,
        body:        'Reenvío automático de factura de proveedor desde CRM.',
        forwardedHtml,
        attachments,
      };

      const res  = await fetch('/.netlify/functions/sendEmail', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() }, body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error al enviar');

      // Marcar en Firestore: factura + resuelto
      await actualizarEstado(thread.latest, {
        isFactura:            true,
        status:               'resuelto',
        facturaReenviadaA:    ADMIN_EMAIL,
        facturaReenviadaEn:   new Date().toISOString(),
        facturaReenviadaPor:  user?.name || '',
      });

      setPendingFactura(null);
      setSelectedThread(null);
    } catch (err) {
      alert(`Error al procesar factura: ${err.message}`);
    } finally {
      setProcesandoFactura(false);
    }
  };

  const abrirRespuesta = (email) => {
    const ccList = (email.cc || []).map(c => c.address).filter(a => a && !a.includes('climatizacionzapata')).join(', ');
    setCompose({ to: email.from?.address || '', cc: ccList, bcc: '', subject: email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`, body: '', attachments: [], forwardedHtml: '' });
    setShowCc(!!ccList); setShowBcc(false);
    setComposing({ mode: 'reply', replyTo: email });
    setSendError('');
  };

  // ── Adjuntos en redactor ──────────────────────────────────────────────────
  const MAX_ATTACH_MB = 10;

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const leidos = await Promise.all(files.map(file => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        filename:    file.name,
        contentType: file.type || 'application/octet-stream',
        size:        file.size,
        content:     reader.result.split(',')[1], // base64 sin prefijo
      });
      reader.readAsDataURL(file);
    })));
    setCompose(p => ({ ...p, attachments: [...p.attachments, ...leidos] }));
    e.target.value = '';
  };

  const removeAttachment = (idx) =>
    setCompose(p => ({ ...p, attachments: p.attachments.filter((_, i) => i !== idx) }));

  const totalAttachMB = (compose.attachments || []).reduce((s, a) => s + a.size, 0) / 1024 / 1024;

  // ── Autocomplete helpers ──────────────────────────────────────────────────
  // Extrae el último fragmento después de la última coma (soporte multi-dirección)
  const getLastFragment = (val) => val.split(',').pop().trim();

  // Sugerencias filtradas por el último fragmento
  const acSuggestions = useMemo(() => {
    if (!acField || acQuery.length < 1) return [];
    const q = acQuery.toLowerCase();
    return knownContacts
      .filter(c => c.address.toLowerCase().includes(q) || (c.name && c.name.toLowerCase().includes(q)))
      .slice(0, 6);
  }, [acField, acQuery, knownContacts]);

  // Al cambiar un campo de destinatario
  const handleRecipientChange = (field, val) => {
    setCompose(p => ({ ...p, [field]: val }));
    setAcField(field);
    setAcQuery(getLastFragment(val));
  };

  // Al seleccionar una sugerencia
  const selectSuggestion = (field, contact) => {
    const current = compose[field] || '';
    const parts   = current.split(',').map(s => s.trim()).filter(Boolean);
    parts.pop(); // quita el fragmento incompleto
    parts.push(contact.address);
    setCompose(p => ({ ...p, [field]: parts.join(', ') + ', ' }));
    setAcField(null);
    setAcQuery('');
  };

  // ── Enviar ────────────────────────────────────────────────────────────────
  const enviarEmail = async () => {
    const { to, cc, bcc, subject, body, attachments, forwardedHtml } = compose;
    if (!to.trim() || !subject.trim() || !body.trim()) { setSendError('Rellena todos los campos.'); return; }
    if (totalAttachMB > MAX_ATTACH_MB) { setSendError(`Los adjuntos superan ${MAX_ATTACH_MB} MB.`); return; }
    setSendError(''); setSending(true);
    try {
      const payload = {
        to: to.trim(), subject: subject.trim(), body: body.trim(),
        forwardedHtml: forwardedHtml || '',
        senderName: user?.name || '',
        ...(cc?.trim()  && { cc:  cc.trim() }),
        ...(bcc?.trim() && { bcc: bcc.trim() }),
        attachments: (attachments || []).map(({ filename, contentType, content }) => ({ filename, contentType, content })),
        ...(composing?.replyTo && {
          inReplyTo:  composing.replyTo.messageId,
          references: [...(composing.replyTo.references || []), composing.replyTo.messageId],
        }),
      };
      const res  = await fetch('/.netlify/functions/sendEmail', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() }, body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) { setSendError(data.error || 'Error al enviar'); return; }

      const docId   = toDocId(data.messageId || `sent-${Date.now()}`);
      const htmlBody = body.trim().replace(/\n/g, '<br/>');
      const ccAddrs = (cc?.trim() || '').split(',').map(s => s.trim()).filter(Boolean).map(a => ({ address: a }));
      await setDoc(doc(db, COL_ENVIADOS, docId), {
        messageId:  data.messageId || docId,
        from:       { address: 'info@climatizacionzapata.es', name: 'Climatización Zapata' },
        to:         [{ address: to.trim() }],
        cc:         ccAddrs,
        subject:    subject.trim(), date: new Date().toISOString(),
        inReplyTo:  composing?.replyTo?.messageId || '',
        references: composing?.replyTo
          ? [...(composing.replyTo.references || []), composing.replyTo.messageId]
          : [],
        sentBy:     user?.name || '',
        htmlBody,
      });
      setComposing(null);
      setCompose({ to: '', cc: '', bcc: '', subject: '', body: '', attachments: [], forwardedHtml: '' });
      setShowCc(false); setShowBcc(false);
    } catch (err) { setSendError(err.message); }
    finally { setSending(false); }
  };

  // ── Helpers de carpeta ────────────────────────────────────────────────────
  const folderInfo = (path) => {
    const f  = folders.find(f => f.path === path);
    const su = f?.specialUse;
    return FOLDER_META[su] || { label: f?.name || path, emoji: '📁' };
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto">

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg border p-4 mb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Icons.Mail className="w-6 h-6 text-red-600 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                CRM Email
                {noLeidos > 0 && <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full">{noLeidos}</span>}
              </h2>
              <p className="text-gray-400 text-xs">
                info@climatizacionzapata.es
                {lastSync && <> · sync {timeAgo(lastSync)}</>}
                {statsRespuesta && (
                  <span className="ml-2 text-gray-300">·</span>
                )}
                {statsRespuesta && (
                  <span className="ml-2 text-gray-400" title={`Tiempo medio de primera respuesta (${statsRespuesta.count} conversacion${statsRespuesta.count !== 1 ? 'es' : ''} últimos 7 días)`}>
                    ⏱ {statsRespuesta.texto}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {facturasCount > 0 && (
              <button onClick={() => setShowFacturasResueltas(v => !v)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${showFacturasResueltas ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-blue-300'}`}>
                {showFacturasResueltas ? 'Ocultar facturas' : `📄 ${facturasCount}`}
              </button>
            )}
            {marketing > 0 && (
              <button onClick={() => setShowMarketing(v => !v)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${showMarketing ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-orange-300'}`}>
                {showMarketing ? 'Ocultar marketing' : `📢 ${marketing}`}
              </button>
            )}
            <button onClick={() => syncEmails(true)} disabled={syncing}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40">
              <Icons.Refresh className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Sync…' : 'Sync'}
            </button>
            <button onClick={() => { setCompose({ to: '', cc: '', bcc: '', subject: '', body: '', attachments: [], forwardedHtml: '' }); setShowCc(false); setShowBcc(false); setComposing({ mode: 'new' }); setSendError(''); }}
              className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-lg text-xs font-medium">
              <Icons.Plus className="w-3.5 h-3.5" /> Redactar
            </button>
          </div>
        </div>
        {syncError && <p className="mt-1.5 text-xs text-red-600 bg-red-50 px-3 py-1 rounded-lg">{syncError}</p>}
      </div>

      {/* Layout 3 columnas */}
      <div className="flex gap-3" style={{ height: 'calc(100vh - 260px)', minHeight: 520 }}>

        {/* ── Columna izquierda (estados + equipo) ────────────────────────── */}
        <div className="hidden lg:flex flex-col gap-3 w-44 flex-shrink-0 overflow-y-auto pb-1">

          {/* Tarjeta estados */}
          <div className="flex flex-col bg-white rounded-2xl shadow border overflow-hidden">
            <div className="px-3 py-2.5 border-b flex-shrink-0">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Estados</p>
            </div>
            <div className="py-1.5 px-1.5 space-y-0.5 overflow-y-auto max-h-52">

              <button onClick={() => setFiltroStatus('todos')}
                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm transition-colors ${filtroStatus === 'todos' ? 'bg-red-50 text-red-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                <span className="flex items-center gap-2 truncate">
                  <span className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0" />
                  <span className="truncate">Todos</span>
                </span>
                <span className={`text-xs rounded-full px-1.5 py-0.5 flex-shrink-0 font-medium ${filtroStatus === 'todos' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                  {statusCounts.total}
                </span>
              </button>

              <button onClick={() => setFiltroStatus('no_leidos')}
                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm transition-colors ${filtroStatus === 'no_leidos' ? 'bg-red-50 text-red-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                <span className="flex items-center gap-2 truncate">
                  <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                  <span className="truncate">No leídos</span>
                </span>
                {noLeidos > 0 && (
                  <span className={`text-xs rounded-full px-1.5 py-0.5 flex-shrink-0 font-medium ${filtroStatus === 'no_leidos' ? 'bg-red-100 text-red-700' : 'bg-red-50 text-red-500'}`}>
                    {noLeidos}
                  </span>
                )}
              </button>

              <div className="border-t border-gray-100 my-1" />

              {Object.entries(STATUS).map(([key, meta]) => (
                <button key={key} onClick={() => setFiltroStatus(key)}
                  className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm transition-colors ${filtroStatus === key ? 'bg-red-50 text-red-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                  <span className="flex items-center gap-2 truncate">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />
                    <span className="truncate">{meta.label}</span>
                  </span>
                  <span className={`text-xs rounded-full px-1.5 py-0.5 flex-shrink-0 font-medium ${
                    filtroStatus === key ? 'bg-red-100 text-red-700' :
                    statusCounts[key] > 0 ? 'bg-gray-100 text-gray-600' : 'text-gray-300'
                  }`}>
                    {statusCounts[key] ?? 0}
                  </span>
                </button>
              ))}

            </div>
          </div>

          {/* Tarjeta equipo */}
          <div className="flex flex-col bg-white rounded-2xl shadow border overflow-hidden">
            <div className="px-3 py-2.5 border-b flex-shrink-0">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Equipo</p>
            </div>
            <div className="py-1.5 px-1.5 space-y-0.5 overflow-y-auto max-h-48">

              <button onClick={() => setFiltroAsignado('todos')}
                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm transition-colors ${filtroAsignado === 'todos' ? 'bg-red-50 text-red-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                <span className="flex items-center gap-2 truncate">
                  <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-400 flex-shrink-0">·</span>
                  <span className="truncate">Todos</span>
                </span>
                <span className={`text-xs rounded-full px-1.5 py-0.5 flex-shrink-0 font-medium ${filtroAsignado === 'todos' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                  {teamCounts.total}
                </span>
              </button>

              <button onClick={() => setFiltroAsignado('sin_asignar')}
                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm transition-colors ${filtroAsignado === 'sin_asignar' ? 'bg-red-50 text-red-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                <span className="flex items-center gap-2 truncate">
                  <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-300 flex-shrink-0">?</span>
                  <span className="truncate">Sin asignar</span>
                </span>
                <span className={`text-xs rounded-full px-1.5 py-0.5 flex-shrink-0 font-medium ${
                  filtroAsignado === 'sin_asignar' ? 'bg-red-100 text-red-700' :
                  teamCounts.sinAsignar > 0 ? 'bg-gray-100 text-gray-600' : 'text-gray-300'
                }`}>
                  {teamCounts.sinAsignar}
                </span>
              </button>

              <div className="border-t border-gray-100 my-1" />

              {trabajadores.map((w, i) => {
                const colors = ['bg-blue-100 text-blue-700', 'bg-purple-100 text-purple-700', 'bg-pink-100 text-pink-700'];
                const isActive = filtroAsignado === w.id;
                const count = teamCounts[w.id] ?? 0;
                return (
                  <button key={w.id} onClick={() => setFiltroAsignado(w.id)}
                    className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm transition-colors ${isActive ? 'bg-red-50 text-red-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                    <span className="flex items-center gap-2 truncate">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${colors[i % colors.length]}`}>
                        {w.nombre.charAt(0)}
                      </span>
                      <span className="truncate">{w.nombre.split(' ')[0]}</span>
                    </span>
                    <span className={`text-xs rounded-full px-1.5 py-0.5 flex-shrink-0 font-medium ${
                      isActive ? 'bg-red-100 text-red-700' :
                      count > 0 ? 'bg-gray-100 text-gray-600' : 'text-gray-300'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}

            </div>
          </div>

          {/* Tarjeta triage */}
          <div className="flex flex-col bg-white rounded-2xl shadow border overflow-hidden">
            <div className="px-3 py-2.5 border-b">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Triage</p>
            </div>
            <div className="py-1.5 px-1.5 space-y-0.5">

              {[
                { key: 'ariston', label: 'Ariston', icon: '🔴', cls: 'bg-red-50 text-red-700',   activeRing: 'ring-1 ring-red-400',  dot: 'bg-red-300'  },
                { key: 'factura', label: 'Factura', icon: '📄', cls: 'bg-blue-50 text-blue-600', activeRing: 'ring-1 ring-blue-400', dot: 'bg-blue-200' },
                { key: 'web',     label: 'Web',     icon: '🌐', cls: 'bg-gray-50 text-gray-500', activeRing: 'ring-1 ring-gray-400', dot: 'bg-gray-300' },
              ].map(({ key, label, icon, cls, dot, activeRing }) => {
                const count    = triageCounts[key] ?? 0;
                const isActive = filtroTriage === key;
                return (
                  <button key={key}
                    onClick={() => setFiltroTriage(isActive ? null : key)}
                    className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm transition-colors ${cls} ${isActive ? activeRing + ' font-bold' : 'hover:brightness-95'}`}>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                      <span className="font-medium">{icon} {label}</span>
                    </span>
                    <span className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${isActive ? 'bg-white/80' : count > 0 ? 'bg-white/70' : 'opacity-30'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}

            </div>
          </div>

        </div>

        {/* ── Lista de hilos ───────────────────────────────────────────────── */}
        <div className={`flex flex-col bg-white rounded-2xl shadow border overflow-hidden flex-shrink-0 w-72 ${selectedThread ? 'hidden lg:flex' : 'flex flex-1 lg:flex-none'}`}>

          {/* Filtros */}
          <div className="p-2 border-b space-y-1.5 flex-shrink-0">
            <div className="relative">
              <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar…"
                className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-red-400" />
            </div>
            <div className="flex gap-1">
              <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
                className="flex-1 border border-gray-200 rounded-lg px-1.5 py-1 text-xs focus:outline-none focus:border-red-400">
                <option value="todos">Estado</option>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={filtroAsignado} onChange={e => setFiltroAsignado(e.target.value)}
                className="flex-1 border border-gray-200 rounded-lg px-1.5 py-1 text-xs focus:outline-none focus:border-red-400">
                <option value="todos">Asignado</option>
                {trabajadores.map(t => <option key={t.id} value={t.id}>{t.nombre.split(' ')[0]}</option>)}
              </select>
            </div>

            {/* Barra de selección múltiple */}
            <div className="flex items-center gap-1.5">
              <input type="checkbox"
                checked={selectedThreadIds.size > 0 && selectedThreadIds.size === threadsFiltrados.slice(0, displayLimit).length}
                ref={el => { if (el) el.indeterminate = selectedThreadIds.size > 0 && selectedThreadIds.size < threadsFiltrados.slice(0, displayLimit).length; }}
                onChange={e => setSelectedThreadIds(e.target.checked ? new Set(threadsFiltrados.slice(0, displayLimit).map(t => t.id)) : new Set())}
                className="w-3.5 h-3.5 accent-red-600 cursor-pointer flex-shrink-0" />
              <span className="text-xs text-gray-500 flex-1">
                {selectedThreadIds.size > 0 ? `${selectedThreadIds.size} seleccionados` : 'Seleccionar todos'}
              </span>
              {selectedThreadIds.size > 0 && (
                <button onClick={() => setSelectedThreadIds(new Set())}
                  className="text-xs text-gray-400 hover:text-gray-600">✕</button>
              )}
            </div>

            {/* Acciones en masa (solo visible con selección) */}
            {selectedThreadIds.size > 0 && (
              <div className="flex gap-1 pt-0.5 border-t border-gray-100">
                <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
                  className="flex-1 border border-gray-200 rounded-lg px-1.5 py-1 text-xs focus:outline-none focus:border-red-400 bg-white">
                  <option value="">Estado…</option>
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select value={bulkAsignado} onChange={e => setBulkAsignado(e.target.value)}
                  className="flex-1 border border-gray-200 rounded-lg px-1.5 py-1 text-xs focus:outline-none focus:border-red-400 bg-white">
                  <option value="">Asignar…</option>
                  {trabajadores.map(t => <option key={t.id} value={t.id}>{t.nombre.split(' ')[0]}</option>)}
                </select>
                <button onClick={aplicarAccionMasiva}
                  disabled={bulkApplying || (!bulkStatus && !bulkAsignado)}
                  className="px-2 py-1 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 rounded-lg transition-colors flex-shrink-0">
                  {bulkApplying ? '…' : 'OK'}
                </button>
              </div>
            )}
          </div>

          {/* Lista de hilos */}
          <div className="flex-1 overflow-y-auto">
            {threadsFiltrados.length === 0 ? (
              <div className="flex items-center justify-center h-24 text-gray-400 text-xs">
                {currentEmails.length === 0 && syncing ? 'Cargando…' : 'Sin resultados'}
              </div>
            ) : threadsFiltrados.slice(0, displayLimit).map(thread => (
              <div key={thread.id}
                className={`flex items-start border-b border-gray-50 hover:bg-gray-50 transition-colors ${selectedThread?.id === thread.id ? 'bg-red-50 border-l-2 border-l-red-500' : ''} ${selectedThreadIds.has(thread.id) ? 'bg-blue-50' : ''}`}>

                {/* Checkbox */}
                <div className="pt-3 pl-2 pr-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <input type="checkbox"
                    checked={selectedThreadIds.has(thread.id)}
                    onChange={e => {
                      setSelectedThreadIds(prev => {
                        const next = new Set(prev);
                        e.target.checked ? next.add(thread.id) : next.delete(thread.id);
                        return next;
                      });
                    }}
                    className="w-3.5 h-3.5 accent-red-600 cursor-pointer" />
                </div>

                <button onClick={() => abrirHilo(thread)} className="flex-1 text-left px-2 py-2.5 min-w-0">

                {/* Fila 1: remitente(s) + hora */}
                <div className="flex items-center justify-between gap-1 mb-0.5">
                  <span className={`text-xs truncate flex-1 ${thread.hasUnread ? 'font-bold text-gray-900' : 'text-gray-600'}`}>
                    {thread.starred && <span className="mr-1">⭐</span>}
                    {thread.names.slice(0, 2).join(', ')}
                    {thread.names.length > 2 && ` +${thread.names.length - 2}`}
                  </span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{timeAgo(thread.latestDate)}</span>
                </div>

                {/* Fila 2: asunto + contador de mensajes */}
                <div className="flex items-center gap-1 mb-1">
                  <p className={`text-xs truncate flex-1 ${thread.hasUnread ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>
                    {thread.subject}
                  </p>
                  {thread.count > 1 && (
                    <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5 flex-shrink-0 font-medium">
                      {thread.count}
                    </span>
                  )}
                </div>

                {/* Fila 3: badges */}
                <div className="flex items-center gap-1 flex-wrap">
                  {thread.hasUnread && <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />}
                  {thread.isAriston       && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold border border-red-200">Ariston</span>}
                  {thread.isInformativo   && <span className="text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded border border-gray-200">Info</span>}
                  {thread.isFactura       && <span className="text-xs bg-blue-100 text-blue-600 px-1 py-0.5 rounded font-medium">📄 Factura</span>}
                  {thread.isWebIncidencia && <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium border border-gray-200">WEB</span>}
                  {thread.triage?.isMarketing && <span className="text-xs bg-orange-100 text-orange-600 px-1 py-0.5 rounded">mktg</span>}
                  {thread.latest.status && thread.latest.status !== 'pendiente' && (
                    <span className={`text-xs px-1 py-0.5 rounded border ${STATUS[thread.latest.status]?.cls}`}>
                      {STATUS[thread.latest.status]?.label}
                    </span>
                  )}
                  {getAssignedTo(thread.latest).map(id => {
                    const w = trabajadores.find(t => t.id === id);
                    return w ? <span key={id} className="text-xs bg-blue-100 text-blue-600 px-1 py-0.5 rounded">{w.nombre.split(' ')[0]}</span> : null;
                  })}
                </div>
                </button>
              </div>
            ))}

            {/* Paginación: primero cliente, luego servidor */}
            {displayLimit < threadsFiltrados.length ? (
              <button onClick={() => setDisplayLimit(d => d + 20)}
                className="w-full py-3 text-xs text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center gap-2 border-t border-gray-100">
                ↑ Ver 20 más ({threadsFiltrados.length - displayLimit} restantes)
              </button>
            ) : (hasMore || loadingMore) ? (
              <button onClick={cargarMas} disabled={loadingMore}
                className="w-full py-3 text-xs text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center gap-2 border-t border-gray-100 disabled:opacity-50">
                {loadingMore
                  ? <><div className="w-3.5 h-3.5 border-2 border-gray-300/30 border-t-gray-400 rounded-full animate-spin" />Cargando…</>
                  : <>↑ Cargar emails más antiguos</>
                }
              </button>
            ) : noMasHistorial ? (
              <p className="w-full py-3 text-xs text-gray-400 text-center border-t border-gray-100 italic">
                No hay emails más antiguos en este período
              </p>
            ) : null}
          </div>
        </div>

        {/* ── Detalle del hilo ─────────────────────────────────────────────── */}
        {selectedThread ? (
          <div className="flex-1 flex flex-col bg-white rounded-2xl shadow border overflow-hidden min-w-0">

            {/* Cabecera del hilo */}
            <div className="px-4 py-3 border-b flex-shrink-0">
              <div className="flex items-start gap-2 mb-2">
                <button onClick={() => setSelectedThread(null)} className="lg:hidden text-gray-400 hover:text-gray-700 mt-0.5 flex-shrink-0">
                  <Icons.ChevronLeft />
                </button>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-gray-900 leading-snug">{selectedThread.subject}</h3>
                  {selectedThread.count > 1 && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {selectedThread.count} mensajes · {selectedThread.names.join(', ')}
                    </p>
                  )}
                </div>
              </div>

              {/* Barra de acciones del hilo */}
              <div className="flex flex-wrap gap-1.5 items-center">
                <button onClick={() => abrirRespuesta(selectedThread.latest)}
                  className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-2.5 py-1 rounded-lg text-xs font-medium">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                  </svg>
                  Responder
                </button>
                <button onClick={() => abrirReenvioHilo(selectedThread)} disabled={forwardLoading}
                  className="flex items-center gap-1 border border-gray-200 px-2.5 py-1 rounded-lg text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40">
                  {forwardLoading
                    ? <div className="w-3.5 h-3.5 border-2 border-gray-300/30 border-t-gray-500 rounded-full animate-spin"/>
                    : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                  }
                  Reenviar hilo
                </button>
                <button onClick={() => toggleReadEmail(selectedThread.latest)}
                  className="border border-gray-200 px-2.5 py-1 rounded-lg text-xs text-gray-600 hover:bg-gray-50">
                  {selectedThread.latest.read ? '✉️ No leído' : '📖 Leído'}
                </button>
                <button onClick={() => toggleStarEmail(selectedThread.latest)}
                  className={`border px-2.5 py-1 rounded-lg text-xs transition-colors ${selectedThread.starred ? 'border-yellow-300 bg-yellow-50 text-yellow-600' : 'border-gray-200 text-gray-400 hover:bg-gray-50'}`}>
                  ⭐
                </button>
                {/* Factura de proveedor */}
                {selectedThread.isFactura && selectedThread.latest.status === 'resuelto' ? (
                  <span className="flex items-center gap-1 border border-blue-200 bg-blue-50 text-blue-600 px-2.5 py-1 rounded-lg text-xs">
                    📄 Reenviada
                  </span>
                ) : (
                  <button
                    onClick={() => setPendingFactura(selectedThread)}
                    title="Marcar como factura y reenviar a administración"
                    className={`flex items-center gap-1 border px-2.5 py-1 rounded-lg text-xs transition-colors ${selectedThread.isFactura ? 'border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100' : 'border-gray-200 text-gray-400 hover:bg-blue-50 hover:text-blue-500 hover:border-blue-200'}`}>
                    📄 {selectedThread.isFactura ? 'Factura' : 'Factura'}
                  </button>
                )}

                {/* Marcar / desmarcar marketing */}
                {selectedThread.triage?.isMarketing ? (
                  <button
                    onClick={() => actualizarEstado(selectedThread.latest, { isMarketing: false })}
                    title="Quitar de marketing"
                    className="flex items-center gap-1 border border-orange-200 bg-orange-50 text-orange-600 px-2.5 py-1 rounded-lg text-xs hover:bg-orange-100 transition-colors">
                    📢 Marketing
                  </button>
                ) : (
                  <button
                    onClick={() => { actualizarEstado(selectedThread.latest, { isMarketing: true }); setSelectedThread(null); }}
                    title="Marcar como marketing y ocultar"
                    className="flex items-center gap-1 border border-gray-200 text-gray-400 px-2.5 py-1 rounded-lg text-xs hover:bg-orange-50 hover:text-orange-500 hover:border-orange-200 transition-colors">
                    📢
                  </button>
                )}
                <button onClick={buscarCliente}
                  className="flex items-center gap-1 border border-gray-200 px-2.5 py-1 rounded-lg text-xs text-gray-600 hover:bg-gray-50">
                  <Icons.Search className="w-3 h-3" /> StelOrder
                </button>
                <select value={selectedThread.latest.status || 'pendiente'}
                  onChange={e => actualizarEstado(selectedThread.latest, { status: e.target.value })}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-red-400">
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <div className="flex items-center gap-1">
                  {trabajadores.map(w => {
                    const current   = getAssignedTo(selectedThread.latest);
                    const isActive  = current.includes(w.id);
                    return (
                      <button key={w.id} onClick={() => {
                        const next = isActive ? current.filter(id => id !== w.id) : [...current, w.id];
                        actualizarEstado(selectedThread.latest, {
                          assignedTo: next.length > 0 ? next : null,
                          assignedBy: user?.name || null,
                          assignedAt: next.length > 0 ? new Date().toISOString() : null,
                        });
                      }}
                      className={`px-2 py-1 text-xs rounded-lg border transition-colors font-medium ${isActive ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200'}`}>
                        {w.nombre.split(' ')[0]}
                      </button>
                    );
                  })}
                </div>

                {/* Toggle vista chat / clásica */}
                <div className="ml-auto flex items-center gap-0.5 border border-gray-200 rounded-lg p-0.5">
                  <button
                    onClick={() => { setThreadViewMode('chat'); localStorage.setItem('crm_thread_view', 'chat'); }}
                    title="Vista chat"
                    className={`p-1.5 rounded-md transition-colors ${threadViewMode === 'chat' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => { setThreadViewMode('classic'); localStorage.setItem('crm_thread_view', 'classic'); }}
                    title="Vista clásica"
                    className={`p-1.5 rounded-md transition-colors ${threadViewMode === 'classic' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Cuerpo del hilo */}
            <div className={`flex-1 overflow-y-auto rounded-b-2xl ${threadViewMode === 'chat' ? 'bg-gray-50' : 'bg-white'}`}>

              {/* Panel StelOrder */}
              {showCliente && (
                <div className={`border rounded-xl overflow-hidden shadow-sm ${threadViewMode === 'chat' ? 'mx-3 mt-3 bg-white' : 'mx-3 mt-3 bg-white'}`}>
                  <div className="bg-gray-50 px-3 py-1.5 flex items-center justify-between border-b">
                    <span className="text-xs font-semibold text-gray-600">{selectedThread.emails[0]?.from?.address}</span>
                    <button onClick={() => setShowCliente(false)} className="text-gray-400 hover:text-gray-600"><Icons.X className="w-3.5 h-3.5" /></button>
                  </div>
                  {loadingCliente ? (
                    <div className="px-4 py-4 text-center text-gray-400 text-xs">Buscando…</div>
                  ) : !cliente?.client ? (
                    <div className="px-4 py-3 text-xs text-gray-500">{cliente?.success === false ? `Error: ${cliente.error}` : 'No encontrado en StelOrder.'}</div>
                  ) : (
                    <div className="p-3 space-y-2">
                      {/* Cabecera cliente */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-gray-900 truncate">{cliente.client.name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {cliente.client.phone && (
                              <span className="flex items-center gap-1 text-xs text-gray-500">
                                {cliente.client.phone}
                                <a href={`https://wa.me/${cliente.client.phone.replace(/\D/g, '').replace(/^0+/, '')}`}
                                  target="_blank" rel="noreferrer"
                                  title="Abrir WhatsApp"
                                  className="text-green-500 hover:text-green-600 flex-shrink-0"
                                  onClick={e => e.stopPropagation()}>
                                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.557 4.122 1.528 5.855L0 24l6.303-1.654A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.885 0-3.655-.52-5.17-1.426l-.37-.22-3.742.981.999-3.648-.242-.375A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                                </a>
                              </span>
                            )}
                            {cliente.client.email && (
                              <span className="text-xs text-gray-400 truncate">{cliente.client.email}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Cliente</span>
                          {onNavigate && (
                            <button
                              onClick={() => {
                                sessionStorage.setItem('zapata_crm_prefill', JSON.stringify({
                                  nombreCompleto: cliente.client.name  || '',
                                  telefono:       cliente.client.phone || '',
                                  email:          cliente.client.email || '',
                                  clienteId:      cliente.client.id    || null,
                                }));
                                onNavigate('incidence');
                              }}
                              className="text-xs bg-red-50 text-red-600 hover:bg-red-100 px-2 py-0.5 rounded-full border border-red-200 transition-colors whitespace-nowrap">
                              + Nueva orden
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Historial de órdenes */}
                      {cliente.orders?.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Historial</p>
                          {cliente.orders.map(o => (
                            o.pdfPath ? (
                              <a key={o.id} href={o.pdfPath} target="_blank" rel="noreferrer"
                                className="flex items-center justify-between bg-gray-50 hover:bg-red-50 hover:border-red-200 rounded-lg px-2.5 py-1.5 border border-transparent transition-colors group cursor-pointer">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5 mb-0.5">
                                    <span className="text-xs font-mono font-bold text-red-700">{o.reference}</span>
                                    <span className="text-xs text-gray-400">{o.date ? new Date(o.date).toLocaleDateString('es-ES') : '—'}</span>
                                  </div>
                                  <p className="text-xs text-gray-600 truncate">{o.description || o.state || '—'}</p>
                                </div>
                                <svg className="w-3.5 h-3.5 text-gray-300 group-hover:text-red-400 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                              </a>
                            ) : (
                              <div key={o.id} className="bg-gray-50 rounded-lg px-2.5 py-1.5">
                                <div className="flex justify-between mb-0.5">
                                  <span className="text-xs font-mono font-bold text-red-700">{o.reference}</span>
                                  <span className="text-xs text-gray-400">{o.date ? new Date(o.date).toLocaleDateString('es-ES') : '—'}</span>
                                </div>
                                <p className="text-xs text-gray-600 truncate">{o.description || o.state || '—'}</p>
                              </div>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Mensajes del hilo */}
              {threadViewMode === 'chat' ? (
              <div className="px-3 py-4 space-y-1">
                {selectedThread.emails.map((email, idx) => {
                  const isExpanded = expandedEmails.has(email.messageId);
                  const body       = threadBodies[email.messageId];
                  const loading    = loadingBodies[email.messageId];
                  const isNuestro  = email.from?.address?.includes('climatizacionzapata');
                  const initial    = remitente(email.from).charAt(0).toUpperCase();

                  // Separador de fecha entre días distintos
                  const prevEmail  = selectedThread.emails[idx - 1];
                  const thisDay    = new Date(email.date).toDateString();
                  const prevDay    = prevEmail ? new Date(prevEmail.date).toDateString() : null;
                  const showDate   = thisDay !== prevDay;
                  const dateLabel  = (() => {
                    const d = new Date(email.date);
                    const today = new Date();
                    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
                    if (d.toDateString() === today.toDateString())     return 'Hoy';
                    if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
                    return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
                  })();

                  // Agrupar mensajes consecutivos del mismo remitente
                  const nextEmail  = selectedThread.emails[idx + 1];
                  const sameNext   = nextEmail?.from?.address === email.from?.address;

                  return (
                    <React.Fragment key={email.messageId}>
                      {showDate && (
                        <div className="flex items-center justify-center my-3">
                          <span className="text-xs text-gray-400 bg-gray-200 px-3 py-0.5 rounded-full capitalize">{dateLabel}</span>
                        </div>
                      )}

                      <div className={`flex items-end gap-2 ${isNuestro ? 'flex-row-reverse' : 'flex-row'}`}>
                        {/* Avatar — solo en el último del grupo */}
                        <div className="w-7 flex-shrink-0 flex items-end pb-0.5">
                          {!sameNext && (
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${isNuestro ? 'bg-red-200 text-red-700' : 'bg-white border border-gray-200 text-gray-600'}`}>
                              {initial}
                            </div>
                          )}
                        </div>

                        {/* Burbuja */}
                        <div className={`max-w-[82%] ${isNuestro ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}>
                          {/* Nombre (solo primer mensaje del grupo) */}
                          {(idx === 0 || selectedThread.emails[idx - 1]?.from?.address !== email.from?.address) && (
                            <span className={`text-xs font-semibold px-1 ${isNuestro ? 'text-right text-red-600' : 'text-gray-500'}`}>
                              {isNuestro ? (email.sentBy || remitente(email.from)) : remitente(email.from)}
                            </span>
                          )}

                          {/* Burbuja principal */}
                          <div
                            onClick={() => toggleExpandEmail(email)}
                            className={`rounded-2xl overflow-hidden cursor-pointer transition-shadow hover:shadow-md
                              ${isNuestro
                                ? `bg-red-600 text-white ${sameNext ? 'rounded-tr-2xl' : 'rounded-tr-sm'}`
                                : `bg-white border border-gray-200 text-gray-800 ${sameNext ? 'rounded-tl-2xl' : 'rounded-tl-sm'}`
                              }
                              ${!email.read && !isNuestro ? 'ring-2 ring-red-300' : ''}
                            `}
                          >
                            {/* Header colapsado */}
                            {!isExpanded && (
                              <div className="px-3.5 py-2.5">
                                <div className={`flex items-center justify-between gap-3`}>
                                  <p className={`text-sm leading-snug line-clamp-2 ${!email.read && !isNuestro ? 'font-semibold' : ''} ${isNuestro ? 'text-white/90' : 'text-gray-700'}`}>
                                    {email.subject}
                                  </p>
                                  {body?.attachments?.length > 0 && (
                                    <Icons.FileText className={`w-3.5 h-3.5 flex-shrink-0 ${isNuestro ? 'text-white/60' : 'text-gray-400'}`} />
                                  )}
                                </div>
                                {(email.cc || []).length > 0 && (
                                  <p className={`text-xs mt-0.5 ${isNuestro ? 'text-white/60' : 'text-gray-400'}`}>
                                    CC: {email.cc.map(t => t.name || t.address).join(', ')}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Cuerpo expandido */}
                            {isExpanded && (
                              <div>
                                {/* Meta */}
                                <div className={`px-3.5 pt-2.5 pb-2 border-b text-xs space-y-0.5 ${isNuestro ? 'border-white/20 text-white/70' : 'border-gray-100 text-gray-400'}`}>
                                  <p><span className="font-semibold">Para:</span> {(email.to || []).map(t => t.name || t.address).join(', ')}</p>
                                  {(email.cc || []).length > 0 && (
                                    <p><span className="font-semibold">CC:</span> {email.cc.map(t => t.name || t.address).join(', ')}</p>
                                  )}
                                </div>

                                {/* Contenido */}
                                <div className={`${isNuestro ? 'bg-white' : 'bg-white'}`}>
                                  {(loading || body === undefined) ? (
                                    <div className="h-24 flex items-center justify-center text-gray-400 text-xs">Cargando…</div>
                                  ) : body?.error ? (
                                    <div className="text-center py-6 text-gray-400 text-sm px-4">
                                      <p>No se pudo cargar el contenido.</p>
                                      <p className="text-xs text-red-400 mt-1">{body.error}</p>
                                      <button onClick={e => { e.stopPropagation();
                                        setThreadBodies(prev => { const n = { ...prev }; delete n[email.messageId]; return n; });
                                        cargarCuerpo(email);
                                      }} className="block mx-auto mt-2 text-red-600 hover:underline text-xs">Reintentar</button>
                                    </div>
                                  ) : body ? (
                                    <>
                                      {body.html ? (
                                        <iframe srcDoc={body.html} sandbox="allow-same-origin"
                                          title={`email-${idx}`}
                                          className="w-full border-0 bg-white"
                                          style={{ height: 320 }}
                                          onLoad={e => {
                                            try {
                                              const h = e.target.contentDocument?.body?.scrollHeight;
                                              if (h) e.target.style.height = Math.min(h + 32, 700) + 'px';
                                            } catch {}
                                          }}
                                        />
                                      ) : (
                                        <div className="px-3.5 py-3">
                                          <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">
                                            {body.text || 'Sin contenido'}
                                          </pre>
                                        </div>
                                      )}

                                      {/* Adjuntos */}
                                      {body.attachments?.length > 0 && (
                                        <div className="px-3.5 pb-3 pt-2 border-t border-gray-100 flex flex-wrap gap-2">
                                          {body.attachments.map((a, i) => {
                                            const attKey = `${email.messageId}-${i}`;
                                            return (
                                              <button key={i} onClick={e => { e.stopPropagation(); downloadAttachment(email, a.filename, i); }}
                                                disabled={downloadingAtt === attKey}
                                                className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-lg px-2.5 py-1.5 transition-colors">
                                                <Icons.FileText className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                                <div className="text-left">
                                                  <p className="text-xs text-gray-700 font-medium truncate max-w-[120px]">{a.filename}</p>
                                                  <p className="text-xs text-gray-400">{Math.round((a.size || 0) / 1024)}KB</p>
                                                </div>
                                                {downloadingAtt === attKey
                                                  ? <div className="w-3 h-3 border-2 border-gray-400/30 border-t-gray-500 rounded-full animate-spin flex-shrink-0" />
                                                  : <Icons.Download className="w-3 h-3 text-gray-400 flex-shrink-0" />}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      )}

                                      {/* Acciones */}
                                      <div className="px-3.5 py-2 border-t border-gray-100 flex gap-3 bg-gray-50">
                                        <button onClick={e => { e.stopPropagation(); abrirRespuesta(email); }}
                                          className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium">
                                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                                          </svg>
                                          Responder
                                        </button>
                                        <button onClick={e => { e.stopPropagation(); abrirReenvioEmail(email); }} disabled={forwardLoading}
                                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40">
                                          {forwardLoading
                                            ? <div className="w-3 h-3 border-2 border-gray-300/30 border-t-gray-400 rounded-full animate-spin"/>
                                            : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                                          }
                                          Reenviar
                                        </button>
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Timestamp */}
                          <span className={`text-xs text-gray-400 px-1 ${isNuestro ? 'text-right' : ''}`}>
                            {new Date(email.date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                            {!email.read && !isNuestro && <span className="ml-1 text-red-400 font-bold">·</span>}
                          </span>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
              ) : (
              /* ── Vista clásica ─────────────────────────────────────────────── */
              <div className="p-3 space-y-2">
                {selectedThread.emails.map((email, idx) => {
                  const isExpanded = expandedEmails.has(email.messageId);
                  const body       = threadBodies[email.messageId];
                  const loading    = loadingBodies[email.messageId];
                  const initial    = remitente(email.from).charAt(0).toUpperCase();
                  const isNuestro  = email.from?.address?.includes('climatizacionzapata');
                  return (
                    <div key={email.messageId}
                      className={`border rounded-xl overflow-hidden transition-shadow ${isExpanded ? 'shadow-sm' : ''}`}>
                      <button onClick={() => toggleExpandEmail(email)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${isExpanded ? 'bg-gray-50 border-b' : ''}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isNuestro ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                          {initial}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className={`text-xs truncate ${!email.read ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                              {remitente(email.from)}{isNuestro && <span className="ml-1 text-red-500 font-normal">(nosotros)</span>}
                            </span>
                            <span className="text-xs text-gray-400 flex-shrink-0">{timeAgo(email.date)}</span>
                          </div>
                          {!isExpanded && (
                            <p className="text-xs text-gray-400 truncate">
                              {new Date(email.date).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                        <Icons.ChevronDown className={`w-4 h-4 text-gray-300 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      {isExpanded && (
                        <div className="p-4">
                          <div className="flex flex-col gap-y-0.5 text-xs text-gray-500 mb-3">
                            <span><strong>De:</strong> {remitente(email.from)} &lt;{email.from?.address}&gt;</span>
                            <span><strong>Para:</strong> {(email.to || []).map(t => t.name ? `${t.name} <${t.address}>` : t.address).join(', ')}</span>
                            {(email.cc || []).length > 0 && (
                              <span><strong>CC:</strong> {email.cc.map(t => t.name ? `${t.name} <${t.address}>` : t.address).join(', ')}</span>
                            )}
                            <span>{new Date(email.date).toLocaleString('es-ES')}</span>
                          </div>
                          {(loading || body === undefined) ? (
                            <div className="h-24 flex items-center justify-center text-gray-400 text-xs">Cargando…</div>
                          ) : body?.error ? (
                            <div className="text-center py-6 text-gray-400 text-sm">
                              <p>No se pudo cargar el contenido.</p>
                              <p className="text-xs text-red-400 mt-1">{body.error}</p>
                              <button onClick={() => {
                                setThreadBodies(prev => { const n = { ...prev }; delete n[email.messageId]; return n; });
                                cargarCuerpo(email);
                              }} className="block mx-auto mt-2 text-red-600 hover:underline text-xs">Reintentar</button>
                            </div>
                          ) : body ? (
                            <>
                              {body.html ? (
                                <iframe srcDoc={body.html} sandbox="allow-same-origin"
                                  title={`email-${idx}`}
                                  className="w-full border-0 rounded-lg bg-white"
                                  style={{ height: 320 }}
                                  onLoad={e => {
                                    try {
                                      const h = e.target.contentDocument?.body?.scrollHeight;
                                      if (h) e.target.style.height = Math.min(h + 32, 700) + 'px';
                                    } catch {}
                                  }}
                                />
                              ) : (
                                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">
                                  {body.text || 'Sin contenido'}
                                </pre>
                              )}
                              {body.attachments?.length > 0 && (
                                <div className="mt-3 pt-3 border-t">
                                  <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
                                    {body.attachments.length} adjunto{body.attachments.length !== 1 ? 's' : ''}
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {body.attachments.map((a, i) => {
                                      const attKey = `${email.messageId}-${i}`;
                                      return (
                                        <button key={i} onClick={() => downloadAttachment(email, a.filename, i)}
                                          disabled={downloadingAtt === attKey}
                                          className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-lg px-3 py-2 transition-colors">
                                          <Icons.FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                          <div className="text-left">
                                            <p className="text-xs text-gray-700 font-medium truncate max-w-[140px]">{a.filename}</p>
                                            <p className="text-xs text-gray-400">{Math.round((a.size || 0) / 1024)}KB</p>
                                          </div>
                                          {downloadingAtt === attKey
                                            ? <div className="w-3.5 h-3.5 border-2 border-gray-400/30 border-t-gray-500 rounded-full animate-spin flex-shrink-0" />
                                            : <Icons.Download className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              <div className="mt-3 pt-3 border-t flex gap-2">
                                <button onClick={() => abrirRespuesta(email)}
                                  className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                                  </svg>
                                  Responder
                                </button>
                                <button onClick={() => abrirReenvioEmail(email)} disabled={forwardLoading}
                                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40">
                                  {forwardLoading
                                    ? <div className="w-3 h-3 border-2 border-gray-300/30 border-t-gray-400 rounded-full animate-spin"/>
                                    : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                                  }
                                  Reenviar este
                                </button>
                              </div>
                            </>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          </div>
        ) : (
          <div className="hidden lg:flex flex-1 items-center justify-center bg-white rounded-2xl shadow border flex-col gap-2 text-gray-300">
            <Icons.Mail className="w-12 h-12 opacity-30" />
            <p className="text-sm">{folderInfo(currentFolder).emoji} {folderInfo(currentFolder).label}</p>
            <p className="text-xs">{threadsFiltrados.length} conversación{threadsFiltrados.length !== 1 ? 'es' : ''}</p>
          </div>
        )}
      </div>

      {/* Modal confirmación factura de proveedor */}
      {pendingFactura && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                📄 Factura de proveedor
              </h3>
              <button onClick={() => setPendingFactura(null)} disabled={procesandoFactura}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"><Icons.X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-700">
                Se reenviará <strong>{pendingFactura.subject}</strong> a{' '}
                <span className="text-blue-600 font-medium">{ADMIN_EMAIL}</span>{' '}
                con todos los adjuntos del hilo.
              </p>
              <p className="text-xs text-gray-500">
                El correo quedará marcado como <span className="font-medium text-green-600">Resuelto</span> y
                desaparecerá de la bandeja principal.
              </p>
              {/* Adjuntos del hilo (de los que ya están cargados) */}
              {(() => {
                const atts = pendingFactura.emails.flatMap(e => {
                  const b = threadBodies[e.messageId];
                  return (b?.attachments || []).map(a => a.filename);
                });
                return atts.length > 0 ? (
                  <div className="bg-blue-50 rounded-lg px-3 py-2">
                    <p className="text-xs font-semibold text-blue-600 mb-1">Adjuntos detectados:</p>
                    {atts.map((name, i) => (
                      <p key={i} className="text-xs text-blue-700 truncate">📎 {name}</p>
                    ))}
                    <p className="text-xs text-blue-400 mt-1 italic">
                      Se descargarán y adjuntarán automáticamente.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">
                    Los adjuntos se incluirán al procesar (si los hay).
                  </p>
                );
              })()}
            </div>
            <div className="px-5 pb-5 flex gap-2">
              <button onClick={() => setPendingFactura(null)} disabled={procesandoFactura}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-30">
                Cancelar
              </button>
              <button onClick={() => procesarFactura(pendingFactura)} disabled={procesandoFactura}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2">
                {procesandoFactura
                  ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Enviando…</>
                  : '📤 Reenviar a administración'
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal decisión adjuntos en reenvío */}
      {pendingForward && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-900 text-sm">Adjuntos en el reenvío</h3>
              <button onClick={() => setPendingForward(null)} className="text-gray-400 hover:text-gray-600">
                <Icons.X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-600">
                Este mensaje contiene {pendingForward.attInfos.length} adjunto{pendingForward.attInfos.length !== 1 ? 's' : ''}.
                ¿Quieres incluirlos en el reenvío?
              </p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {pendingForward.attInfos.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                    <Icons.FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="text-xs text-gray-700 truncate flex-1">{a.filename}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{(a.size / 1024).toFixed(0)} KB</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-2">
              <button onClick={() => setPendingForward(null)}
                className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={() => abrirComposeConReenvio(pendingForward.subject, pendingForward.forwardedHtml)}
                className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">
                Sin adjuntos
              </button>
              <button
                disabled={fetchingAttachments}
                onClick={async () => {
                  setFetchingAttachments(true);
                  try {
                    const attachments = await fetchAttachmentsForForward(pendingForward.attInfos);
                    abrirComposeConReenvio(pendingForward.subject, pendingForward.forwardedHtml, attachments);
                  } finally { setFetchingAttachments(false); }
                }}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2">
                {fetchingAttachments
                  ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Descargando…</>
                  : 'Con adjuntos'
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal redactar / responder / reenviar */}
      {composing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-3.5 border-b flex-shrink-0">
              <h3 className="font-bold text-gray-900 text-sm">
                {composing.mode === 'reply' ? 'Responder' : composing.mode === 'forward' ? 'Reenviar' : 'Nuevo email'}
              </h3>
              <button onClick={() => setComposing(null)} className="text-gray-400 hover:text-gray-700"><Icons.X /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-600">Para *</label>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => setShowCc(v => !v)}
                      className={`text-xs px-2 py-0.5 rounded font-medium border transition-colors ${showCc ? 'bg-red-50 border-red-300 text-red-600' : 'border-gray-200 text-gray-400 hover:text-gray-600'}`}>
                      CC
                    </button>
                    <button type="button" onClick={() => setShowBcc(v => !v)}
                      className={`text-xs px-2 py-0.5 rounded font-medium border transition-colors ${showBcc ? 'bg-red-50 border-red-300 text-red-600' : 'border-gray-200 text-gray-400 hover:text-gray-600'}`}>
                      CCO
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <input value={compose.to}
                    onChange={e => handleRecipientChange('to', e.target.value)}
                    onFocus={() => { setAcField('to'); setAcQuery(getLastFragment(compose.to)); }}
                    onBlur={() => setTimeout(() => setAcField(null), 150)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                    placeholder="destinatario@ejemplo.com" autoComplete="off" />
                  {acField === 'to' && acSuggestions.length > 0 && (
                    <RecipientSuggestions suggestions={acSuggestions} onSelect={c => selectSuggestion('to', c)} />
                  )}
                </div>
              </div>
              {showCc && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">CC <span className="font-normal text-gray-400">(separar con comas)</span></label>
                  <div className="relative">
                    <input value={compose.cc}
                      onChange={e => handleRecipientChange('cc', e.target.value)}
                      onFocus={() => { setAcField('cc'); setAcQuery(getLastFragment(compose.cc)); }}
                      onBlur={() => setTimeout(() => setAcField(null), 150)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                      placeholder="cc1@ejemplo.com, cc2@ejemplo.com" autoComplete="off" />
                    {acField === 'cc' && acSuggestions.length > 0 && (
                      <RecipientSuggestions suggestions={acSuggestions} onSelect={c => selectSuggestion('cc', c)} />
                    )}
                  </div>
                </div>
              )}
              {showBcc && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">CCO <span className="font-normal text-gray-400">(copia oculta)</span></label>
                  <div className="relative">
                    <input value={compose.bcc}
                      onChange={e => handleRecipientChange('bcc', e.target.value)}
                      onFocus={() => { setAcField('bcc'); setAcQuery(getLastFragment(compose.bcc)); }}
                      onBlur={() => setTimeout(() => setAcField(null), 150)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                      placeholder="bcc@ejemplo.com" autoComplete="off" />
                    {acField === 'bcc' && acSuggestions.length > 0 && (
                      <RecipientSuggestions suggestions={acSuggestions} onSelect={c => selectSuggestion('bcc', c)} />
                    )}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Asunto *</label>
                <input value={compose.subject} onChange={e => setCompose(p => ({ ...p, subject: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Mensaje *</label>
                <textarea value={compose.body} onChange={e => setCompose(p => ({ ...p, body: e.target.value }))}
                  rows={8} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 resize-none"
                  placeholder="Escribe tu mensaje…" />
              </div>
              {/* Adjuntos */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-600">Adjuntos</label>
                  <button type="button" onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium">
                    <Icons.Plus className="w-3.5 h-3.5" /> Añadir archivo
                  </button>
                </div>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
                {compose.attachments?.length > 0 ? (
                  <div className="space-y-1">
                    {compose.attachments.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                        <Icons.FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-700 truncate flex-1">{a.filename}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{(a.size / 1024).toFixed(0)}KB</span>
                        <button onClick={() => removeAttachment(i)} className="text-gray-300 hover:text-red-500 flex-shrink-0">
                          <Icons.X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <p className={`text-xs ${totalAttachMB > MAX_ATTACH_MB ? 'text-red-500' : 'text-gray-400'}`}>
                      Total: {totalAttachMB.toFixed(1)} MB / {MAX_ATTACH_MB} MB
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-300 italic">Sin adjuntos</p>
                )}
              </div>

              <div className="border-t pt-3">
                <p className="text-xs text-gray-400 mb-1">Firma (automática)</p>
                <div className="text-xs text-gray-500 border-l-2 border-red-400 pl-3">
                  <strong className="text-gray-700">Climatización Zapata</strong><br/>
                  info@climatizacionzapata.es · climatizacionzapata.es
                </div>
              </div>
              {/* Contenido reenviado */}
              {compose.forwardedHtml && (
                <div className="border rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 flex items-center justify-between border-b">
                    <span className="text-xs font-semibold text-gray-500">Contenido reenviado</span>
                    <button onClick={() => setCompose(p => ({ ...p, forwardedHtml: '' }))}
                      className="text-xs text-gray-400 hover:text-red-500">Eliminar</button>
                  </div>
                  <div className="p-3 max-h-48 overflow-y-auto text-xs text-gray-500"
                    dangerouslySetInnerHTML={{ __html: compose.forwardedHtml }} />
                </div>
              )}

              {sendError && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{sendError}</p>}
            </div>
            <div className="flex gap-3 px-5 pb-5 flex-shrink-0">
              <button onClick={() => setComposing(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={enviarEmail} disabled={sending}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2">
                {sending
                  ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Enviando…</>
                  : <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>Enviar</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
